import { Link } from 'react-router-dom'
import CharacterClassIcon from './CharacterClassIcon'
import { cn } from '@/lib/utils'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import BlizzardIcon from './BlizzardIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CharacterRoleIcon from './CharacterRoleIcon'
import { useTeam } from 'app/frontend/base/config/context/team'

export type RankColorPresentation =
  | 'replace_class_color'
  | 'replace_class_icon'
  | 'show_as_letter'
  | 'show_as_orb'
  | 'replace_icon_border'
  | 'hidden'

type Props = {
  member?: wowaudit.TeamMembership
  characterReference?: wowaudit.CharacterReference
  compact?: boolean
  customSpecDetails?: wowaudit.TeamMembership['specDetails']
  withTooltip?: boolean
  iconType?: 'class' | 'role' | 'none' | 'both'
  rankColorPresentationOverride?: RankColorPresentation
  disableNavigation?: boolean
}

const TeamMemberLink = ({
  member,
  characterReference,
  compact,
  customSpecDetails,
  withTooltip,
  iconType = 'class',
  rankColorPresentationOverride,
  disableNavigation,
}: Props) => {
  if (!member && !characterReference) return null
  const { team } = useTeam()
  const character = (member?.characterReference ??
    characterReference) as wowaudit.CharacterReference

  const specDetails = customSpecDetails ?? member?.specDetails
  const rankColor = member?.teamRank?.color
  const rankName = member?.teamRank?.name?.trim()
  const rankPresentation = rankColorPresentationOverride
    ? rankColorPresentationOverride
    : team?.settings?.rankColorPresentation?.value
  const hasRankColor = !!rankColor
  const rankLetter = (member?.teamRank?.name || character.name || '?')
    .trim()
    .charAt(0)
    .toUpperCase()
  const showBorderOnClassIcon =
    hasRankColor && rankPresentation === 'replace_icon_border'

  const rankBadge = hasRankColor ? (
    <span
      className={cn(
        'inline-flex items-center justify-center font-semibold border text-zinc-950 cursor-default',
        compact
          ? 'h-4 min-w-4 rounded text-[10px] px-1'
          : 'h-5 min-w-5 rounded-md text-xs px-1',
      )}
      style={{ backgroundColor: rankColor ?? undefined }}
    >
      {rankLetter}
    </span>
  ) : null

  const rankOrb = hasRankColor ? (
    <span
      className={cn(
        'inline-flex rounded-full border',
        compact ? 'h-2.5 w-2.5 min-w-2.5 min-h-2.5' : 'h-3 w-3 min-w-3 min-h-3',
      )}
      style={{
        backgroundColor: rankColor ?? undefined,
      }}
    />
  ) : null

  const classIcon = specDetails ? (
    <BlizzardIcon
      size={compact ? 'small' : 'medium'}
      iconName={specDetails.icon}
      className={showBorderOnClassIcon ? 'border-transparent' : undefined}
    />
  ) : (
    <CharacterClassIcon
      size={compact ? 'small' : 'medium'}
      characterClass={character.characterClass}
      className={showBorderOnClassIcon ? 'border-transparent' : undefined}
    />
  )
  const classIconWithBorder = showBorderOnClassIcon ? (
    <span
      className={cn(
        'inline-flex rounded-md p-px',
        compact ? 'rounded-sm' : 'rounded-md',
      )}
      style={{ backgroundColor: rankColor ?? undefined }}
    >
      {classIcon}
    </span>
  ) : (
    classIcon
  )
  const classIconPresentation =
    hasRankColor && rankPresentation === 'replace_class_icon'
      ? rankBadge
      : classIconWithBorder

  const roleIcon = member ? (
    <CharacterRoleIcon role={member.role} size={compact ? 'small' : 'medium'} />
  ) : null

  const icon =
    iconType === 'class' ? (
      classIconPresentation
    ) : iconType === 'role' ? (
      roleIcon
    ) : iconType === 'both' ? (
      <>
        {roleIcon}
        {classIconPresentation}
      </>
    ) : null

  const showRankOrb = hasRankColor && rankPresentation === 'show_as_orb'
  const showRankLetter = hasRankColor && rankPresentation === 'show_as_letter'
  const replaceClassColor =
    hasRankColor && rankPresentation === 'replace_class_color'
  const showIconTooltip =
    !!icon && (withTooltip || (showBorderOnClassIcon && !!rankName))

  return (
    <div
      className={cn('flex items-center min-w-0', compact ? 'gap-1.5' : 'gap-2')}
    >
      {icon ? (
        showIconTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex items-center shrink-0',
                  compact ? 'gap-1.5' : 'gap-2',
                )}
              >
                {icon}
              </span>
            </TooltipTrigger>
            <TooltipContent className='text-center'>
              <div>
                {specDetails
                  ? `${specDetails.name} ${character.characterClass}`
                  : character.characterClass}
              </div>
              {showBorderOnClassIcon && rankName ? (
                <div className='text-muted-foreground text-xs'>{rankName}</div>
              ) : null}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span
            className={cn(
              'inline-flex items-center shrink-0',
              compact ? 'gap-1.5' : 'gap-2',
            )}
          >
            {icon}
          </span>
        )
      ) : null}
      {showRankOrb ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{rankOrb}</span>
          </TooltipTrigger>
          <TooltipContent>{rankName || 'Rank'}</TooltipContent>
        </Tooltip>
      ) : null}
      {showRankLetter ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{rankBadge}</span>
          </TooltipTrigger>
          <TooltipContent>{rankName || 'Rank'}</TooltipContent>
        </Tooltip>
      ) : null}
      {disableNavigation ? (
        <span
          className={cn(
            'truncate min-w-0 block max-w-full',
            !replaceClassColor && classColours[character.characterClass].text,
          )}
          style={
            replaceClassColor ? { color: rankColor ?? undefined } : undefined
          }
        >
          {character.name}
        </span>
      ) : (
        <Link
          to={character.path}
          className={cn(
            'truncate min-w-0 block max-w-full',
            !replaceClassColor && classColours[character.characterClass].text,
          )}
          style={
            replaceClassColor ? { color: rankColor ?? undefined } : undefined
          }
        >
          {character.name}
        </Link>
      )}
    </div>
  )
}

export default TeamMemberLink
